/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import React from "react"
import { useLocation, useHistory } from "react-router-dom"
import { Spacer, Grid } from "@cd2/components"
import { P, TextBlock, H2 } from "@cd2/text"
import { SVGIcon } from "./SVGIcon"
import { PageNotFound } from "../pages/front/PageNotFound"
import { PixelButton } from "./PixelButton"

interface IProps {
  signedIn?: boolean
  children: React.ReactNode
  links?: { to: string; children: string }[]
}
export function ErrorBoundary({ children, links }: IProps) {
  const location = useLocation()
  return (
    <ErrorBoundaryClass pathname={location.pathname} links={links}>
      {children}
    </ErrorBoundaryClass>
  )
}

class ErrorBoundaryClass extends React.Component<{ pathname: string } & IProps> {
  state = {
    hasError: false,
    error: null as Error | null,
    info: undefined as undefined | Record<string, unknown>,
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error | null, info: any) {
    console.error(error, info)
    this.setState({ error, info })
  }

  componentDidUpdate(prevProps: { pathname: string } & IProps) {
    if (this.state.hasError && prevProps.pathname !== this.props.pathname) {
      this.setState({ hasError: false, error: null })
    }
  }

  onClearError() {
    this.setState({ hasError: false, error: null })
  }

  render() {
    const { hasError, error } = this.state

    if (error?.message.includes("No app found")) {
      return <PageNotFound />
    }
    return hasError ? (
      <ErrorPage onClearError={this.onClearError} links={this.props.links} />
    ) : (
      this.props.children
    )
  }
}

function ErrorPage({
  onClearError,
  links,
}: {
  onClearError(): void
  links?: { to: string; children: string }[]
}) {
  const history = useHistory()

  function handleLinkClick(path: string) {
    history.push(path)
    onClearError()
  }

  return (
    <Grid
      columns={["auto"]}
      css={css`
        text-align: center;
        height: 70vh;
        place-content: center;
      `}
    >
      <div
        css={css`
          max-width: 400px;
        `}
      >
        <ErrorTag />
        <Spacer />
        <div
          css={css`
            align-self: center;
            display: flex;
            gap: 8px;
            justify-content: center;
          `}
        >
          <PixelButton variant="white" onClick={() => handleLinkClick("/")}>
            To homepage
          </PixelButton>
          {(links || []).map(link => (
            <PixelButton key={link.to} variant="white" onClick={() => handleLinkClick(link.to)}>
              {link.children}
            </PixelButton>
          ))}
        </div>
      </div>
    </Grid>
  )
}

export function ErrorTag({ h = 1 }: { h?: 1 | 2 | 3 | 4 }) {
  return (
    <TextBlock align="center">
      <H2 h={h}>An error has occurred</H2>
      <div
        css={css`
          width: 100px;
          margin: 30px auto;
        `}
      >
        <SVGIcon icon="error" />
      </div>
      <P>
        Sorry! It seems something has gone wrong. Please try again and contact support if the issue
        persists.
      </P>
    </TextBlock>
  )
}
